import React, { useEffect, useState } from 'react';
import { Calendar } from '../../ui';
import ZoomedChartView from '../ZoomedChartView';
import { issuesAndTasksApi } from '../issuesAndTasksApi';

const DelayAnalysisZoomed = () => {
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [weekRange, setWeekRange] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [labels, setLabels] = useState([]);

  // Get week start (Sunday) and end (Saturday) for a given date
  const getWeekRange = (date) => {
    const startOfWeek = new Date(date);
    const day = startOfWeek.getDay();
    const diff = startOfWeek.getDate() - day;
    startOfWeek.setDate(diff);
    
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);
    
    return { startOfWeek, endOfWeek };
  };

  // Format date for display
  const formatDate = (date) => {
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Format date for API (YYYY-MM-DD)
  const formatDateForAPI = (date) => {
    return date.toISOString().split('T')[0];
  };

  // Fetch data
  useEffect(() => {
    const { startOfWeek, endOfWeek } = getWeekRange(selectedDate);
    const weekRangeString = `${formatDate(startOfWeek)} to ${formatDate(endOfWeek)}`;
    setWeekRange(weekRangeString);
    
    const fetchDataAsync = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const dateStr = formatDateForAPI(selectedDate);

        // Fetch data
        const [sites, delayRes] = await Promise.all([
          issuesAndTasksApi.fetchAllSites(),
          issuesAndTasksApi.getWeeklyAverageDelays(dateStr),
        ]);

        // Map site IDs to names
        const siteIdToNameMap = {};
        sites.forEach(site => {
          siteIdToNameMap[site.site_id] = site.site_name;
        });

        // Map data and convert minutes to hours
        const delayDataArray = [];
        const labelsArray = [];
        
        if (delayRes.average_delay_minutes_per_site) {
          Object.entries(delayRes.average_delay_minutes_per_site).forEach(([siteId, minutes]) => {
            const siteName = siteIdToNameMap[siteId] || siteId;
            delayDataArray.push({ value: minutes / 60.0 });
            labelsArray.push(siteName);
          });
        }

        setData(delayDataArray);
        setLabels(labelsArray);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message || 'Failed to load data. Please try again.');
        setIsLoading(false);
      }
    };
    
    fetchDataAsync();
  }, [selectedDate]);

  // Handle date selection
  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const toggleCalendar = () => {
    setIsCalendarOpen(!isCalendarOpen);
  };

  return (
    <>
      <ZoomedChartView
        title="Delay Analysis"
        yAxisLabel="Delay Time(hours)"
        xAxisLabel="Sites"
        barColor="#22c55e"
        data={data}
        labels={labels}
        weekRange={weekRange}
        selectedDate={selectedDate}
        onDateSelect={handleDateSelect}
        isCalendarOpen={isCalendarOpen}
        onCalendarToggle={toggleCalendar}
        isLoading={isLoading}
      />

      {/* Calendar Modal */}
      <Calendar
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        onDateSelect={handleDateSelect}
        selectedDate={selectedDate}
      />
    </>
  );
};

export default DelayAnalysisZoomed;

